/**
 * Location Management Dialog Component
 *
 * Manages physical bin locations within a warehouse. Mirrors the backend
 * bin_locations model 1:1 (#1166): a bin belongs to one warehouse and carries
 * a code plus optional zone/aisle/rack/shelf coordinates. Warehouses
 * themselves are provisioned elsewhere; this dialog only edits their bins.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Grid,
  Alert,
  InputAdornment,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import type { WarehouseItem } from '@/hooks/useInventory';

/** A single bin location as returned inside a warehouse by GET /warehouses. */
export interface BinLocation {
  id: string;
  code: string;
  description: string | null;
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  shelf: string | null;
}

export interface BinLocationFormData {
  code: string;
  description: string;
  zone: string;
  aisle: string;
  rack: string;
  shelf: string;
}

interface LocationManagementDialogProps {
  open: boolean;
  onClose: () => void;
  warehouses: WarehouseItem[];
  onCreate: (warehouseId: string, data: BinLocationFormData) => void;
  onUpdate: (id: string, data: BinLocationFormData) => void;
  onDelete: (id: string) => void;
}

const EMPTY_FORM: BinLocationFormData = {
  code: '',
  description: '',
  zone: '',
  aisle: '',
  rack: '',
  shelf: '',
};

export function LocationManagementDialog({
  open,
  onClose,
  warehouses,
  onCreate,
  onUpdate,
  onDelete,
}: LocationManagementDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');

  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingBin, setEditingBin] = useState<BinLocation | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<BinLocationFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [deleteBinId, setDeleteBinId] = useState<string | null>(null);

  // Default the warehouse picker to the tenant default (or first) whenever the
  // dialog opens; reset the transient form state on close.
  useEffect(() => {
    if (open) {
      setSelectedWarehouseId((prev) =>
        prev || warehouses.find((w) => w.isDefault)?.id || warehouses[0]?.id || '',
      );
    } else {
      setSearchQuery('');
      setEditingBin(null);
      setFormOpen(false);
      setDeleteBinId(null);
    }
  }, [open, warehouses]);

  const selectedWarehouse = warehouses.find((w) => w.id === selectedWarehouseId);
  const bins = selectedWarehouse?.binLocations ?? [];

  const filteredBins = bins.filter(
    (b) =>
      b.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAddNew = () => {
    setEditingBin(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setIsDirty(false);
    setFormOpen(true);
  };

  const handleEdit = (bin: BinLocation) => {
    setEditingBin(bin);
    setFormData({
      code: bin.code,
      description: bin.description ?? '',
      zone: bin.zone ?? '',
      aisle: bin.aisle ?? '',
      rack: bin.rack ?? '',
      shelf: bin.shelf ?? '',
    });
    setErrors({});
    setIsDirty(false);
    setFormOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteBinId) {
      onDelete(deleteBinId);
      setDeleteBinId(null);
    }
  };

  const handleFormChange = (field: keyof BinLocationFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.code.trim()) {
      newErrors.code = t('locations.validation.codeRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveBin = () => {
    if (!validate()) return;

    if (editingBin) {
      onUpdate(editingBin.id, formData);
    } else if (selectedWarehouseId) {
      onCreate(selectedWarehouseId, formData);
    }

    setFormOpen(false);
    setEditingBin(null);
    setIsDirty(false);
  };

  const resetForm = () => {
    setFormOpen(false);
    setEditingBin(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setIsDirty(false);
  };

  const handleDialogClose = () => {
    if (formOpen) {
      resetForm();
    } else {
      onClose();
    }
  };

  // "Zone A · Aisle 3 · Rack 2 · Shelf 1" from whichever coordinates are set.
  const formatCoordinates = (bin: BinLocation): string => {
    const parts = [
      bin.zone && `${t('locations.form.zone')} ${bin.zone}`,
      bin.aisle && `${t('locations.form.aisle')} ${bin.aisle}`,
      bin.rack && `${t('locations.form.rack')} ${bin.rack}`,
      bin.shelf && `${t('locations.form.shelf')} ${bin.shelf}`,
    ].filter(Boolean);
    return parts.join(' · ');
  };

  return (
    <FormDialog
      open={open}
      onClose={handleDialogClose}
      title={t('locations.title')}
      headerIcon={<WarehouseIcon />}
      isDirty={formOpen && isDirty}
      maxWidth="md"
      renderActions={({ onClose: handleCloseAction }) => (
        <DialogActions sx={{ px: 3, py: 2 }}>
          {formOpen ? (
            <>
              <Button onClick={handleCloseAction} color="inherit">
                {tc('cancel')}
              </Button>
              <Button variant="contained" onClick={handleSaveBin}>
                {editingBin ? t('locations.saveChanges') : t('locations.addLocation')}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseAction}>{tc('close')}</Button>
          )}
        </DialogActions>
      )}
    >
      <DialogContent dividers>
        {!formOpen ? (
          <Box>
            {/* Warehouse picker */}
            <TextField
              select
              fullWidth
              label={t('locations.warehouse')}
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
              sx={{ mb: 3 }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name} ({w.code})
                </MenuItem>
              ))}
            </TextField>

            {/* Search and Add */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder={t('locations.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                disabled={!selectedWarehouseId}
              >
                {tc('add')}
              </Button>
            </Box>

            {/* Bin list */}
            {filteredBins.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <LocationOnIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  {searchQuery ? t('locations.noSearchResults') : t('locations.noLocations')}
                </Typography>
                {!searchQuery && selectedWarehouseId && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddNew}
                    sx={{ mt: 2 }}
                  >
                    {t('locations.addFirst')}
                  </Button>
                )}
              </Box>
            ) : (
              <List>
                {filteredBins.map((bin, index) => (
                  <Box key={bin.id}>
                    {index > 0 && <Divider />}
                    <ListItem sx={{ py: 2 }}>
                      <Box sx={{ mr: 2 }}>
                        <LocationOnIcon fontSize="small" />
                      </Box>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                              {bin.code}
                            </Typography>
                            {formatCoordinates(bin) && (
                              <Chip label={formatCoordinates(bin)} size="small" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          bin.description ? (
                            <Typography variant="body2" color="text.secondary">
                              {bin.description}
                            </Typography>
                          ) : undefined
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={tc('edit')}>
                          <IconButton
                            edge="end"
                            aria-label={tc('edit')}
                            onClick={() => handleEdit(bin)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={tc('delete')}>
                          <IconButton
                            edge="end"
                            aria-label={tc('delete')}
                            onClick={() => setDeleteBinId(bin.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              {editingBin
                ? t('locations.editingLocation', { name: editingBin.code })
                : t('locations.addNew', { warehouse: selectedWarehouse?.name ?? '' })}
            </Alert>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('locations.form.code')}
                  value={formData.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  required
                  fullWidth
                  autoFocus
                  error={!!errors.code}
                  helperText={errors.code || t('locations.form.codeHint')}
                  slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                />
              </Grid>

              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('locations.form.zone')}
                  value={formData.zone}
                  onChange={(e) => handleFormChange('zone', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('locations.form.aisle')}
                  value={formData.aisle}
                  onChange={(e) => handleFormChange('aisle', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('locations.form.rack')}
                  value={formData.rack}
                  onChange={(e) => handleFormChange('rack', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  label={t('locations.form.shelf')}
                  value={formData.shelf}
                  onChange={(e) => handleFormChange('shelf', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('locations.form.description')}
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={t('locations.form.descriptionPlaceholder')}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteBinId}
        title={tc('delete')}
        message={t('locations.confirmDelete')}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteBinId(null); }}
      />
    </FormDialog>
  );
}
